import * as React from 'react';

import {
  DataTable,
  Snackbar,
  Tooltip,
  defaultSnackbarTimeout,
  DateTimePickerInput
} from '../../../../../Shared/index';
import useSnackbarMonitor from '../../../../../Shared/hooks/useSnackbarMonitor'
import {
  DUE_DATE_DEFAULT_TEXT,
  INVALID_DATES_SNACKBAR_COPY,
  PUBLISH_DATE_DEFAULT_TEXT,
  CopyToAllButton,
} from '../../../../helpers/unitActivityDates';
import {
  activityTableHeader,
  previouslyAssignedTableHeader,
  publishDateTableHeader,
  renderActivityName,
  renderToolIcon,
  requestPreviouslyAssignedActivityData,
  toolTableHeader,
} from '../../activity_table_elements';
import PreviouslyAssignedTooltip from '../../previouslyAssignedTooltip';

const PUBLISH_DATE_ATTRIBUTE_KEY = 'publishDates'
const DUE_DATE_ATTRIBUTE_KEY = 'dueDates'

const tableHeaders = [
  toolTableHeader,
  activityTableHeader,
  {
    name: 'Activity Focus',
    attribute: 'activityFocus',
    width: '220px',
    noTooltip: true
  },
  publishDateTableHeader,
  {
    ariaLabel: 'Due Date',
    name: (
      <Tooltip
        tooltipText="Display a due date to students for this activity. Students can still access an activity after the due date."
        tooltipTriggerText={<span className="due-date-header">Due Date</span>}
      />
    ),
    attribute: 'dueDatePicker',
    width: '148px',
    headerClassName: 'due-date-header-container',
    rowSectionClassName: 'datetime-picker'
  },
  previouslyAssignedTableHeader,
]

const ReviewActivities = ({ activities, toggleActivitySelection, assignActivityDate, publishDates, dueDates, }) => {
  const [snackbarVisible, setSnackbarVisible] = React.useState(false)
  const [erroredActivityIds, setErroredActivityIds] = React.useState([])
  const [previouslyAssignedActivityData, setPreviouslyAssignedActivityData] = React.useState(null)

  useSnackbarMonitor(snackbarVisible, setSnackbarVisible, defaultSnackbarTimeout)

  React.useEffect(() => {
    if (!activities || previouslyAssignedActivityData) { return }

    getPreviouslyAssignedActivityData()
  }, [activities, previouslyAssignedActivityData])

  const getPreviouslyAssignedActivityData = () => {
    requestPreviouslyAssignedActivityData(activities.map(activity => activity.id), setPreviouslyAssignedActivityData)
  }

  const handleDateChange = (date, id, dateAttributeKey) => {
    const activity = activities.find(act => act.id === id)
    setErroredActivityIds([])
    assignActivityDate(activity, date, dateAttributeKey);
  }

  const triggerSnackbar = () => { setSnackbarVisible(true) }

  const handleDueDateChange = (date, id) => {
    const dueDateIsBeforePublishDate = date && publishDates[id] && date <= publishDates[id]
    if (dueDateIsBeforePublishDate) {
      setErroredActivityIds([id])
      triggerSnackbar()
    } else {
      handleDateChange(date, id, DUE_DATE_ATTRIBUTE_KEY)
    }
  }

  const handlePublishDateChange = (date, id) => {
    const publishDateIsAfterDueDate = date && dueDates[id] && date > dueDates[id]
    if (publishDateIsAfterDueDate) {
      setErroredActivityIds([id])
      triggerSnackbar()
    } else {
      handleDateChange(date, id, PUBLISH_DATE_ATTRIBUTE_KEY)
    }
  }

  const copyPublishDateToAll = () => {
    const publishDate = publishDates[activities[0].id]
    const activitiesWithEarlierDueDates = activities.filter(act => dueDates[act.id] && (publishDate >= dueDates[act.id]))

    if (publishDate && activitiesWithEarlierDueDates.length) {
      setErroredActivityIds(activitiesWithEarlierDueDates.map(a => a.id))
      triggerSnackbar()
    } else {
      copyDateToAll(PUBLISH_DATE_ATTRIBUTE_KEY)
    }
  }

  const copyDueDateToAll = () => {
    const dueDate = dueDates[activities[0].id]
    const activitiesWithEarlierDueDates = activities.filter(act => publishDates[act.id] && (dueDate < publishDates[act.id]))

    if (dueDate && activitiesWithEarlierDueDates.length) {
      setErroredActivityIds(activitiesWithEarlierDueDates.map(a => a.id))
      triggerSnackbar()
    } else {
      copyDateToAll(DUE_DATE_ATTRIBUTE_KEY)
    }
  }

  const copyDateToAll = (dateAttributeKey) => {
    const dateAttributeKeyToDates = { 'publishDates': publishDates, 'dueDates': dueDates, }
    const existingDate = dateAttributeKeyToDates[dateAttributeKey][activities[0].id]
    setErroredActivityIds([])
    activities.forEach(a => assignActivityDate(a, existingDate, dateAttributeKey))
  }

  const removeRow = id => {
    const activity = activities.find(act => act.id === id)
    toggleActivitySelection(activity, false)
  };

  const rows = () => {
    return activities.flatMap((activity, i) => {
      const {
        activity_focus_name,
        activity_focus_category_name,
        id,
      } = activity;

      const dueDatePicker = (
        <DateTimePickerInput
          closeFunction={(date) => handleDueDateChange(date, id)}
          defaultText={DUE_DATE_DEFAULT_TEXT}
          initialValue={dueDates[id]}
          keyForElement={`dueDate${id}`}
        />
      );

      const publishDatePicker = (
        <DateTimePickerInput
          closeFunction={(date) => handlePublishDateChange(date, id)}
          defaultText={PUBLISH_DATE_DEFAULT_TEXT}
          initialValue={publishDates[id]}
          keyForElement={`publishDate${id}`}
        />
      );

      const toolIcon = renderToolIcon(activity);

      const activityName = renderActivityName(activity);

      const activityFocus = (
        <span className="activity-focus-cell">
          {activity_focus_category_name && <span className="focus-category-prefix">{activity_focus_category_name}</span>}
          <span className="focus-name">{activity_focus_name}</span>
        </span>
      );

      const dataRow = {
        id,
        className: erroredActivityIds.includes(id) ? 'checked' : '',
        tool: toolIcon,
        activity: activityName,
        activityFocus,
        dueDatePicker,
        publishDatePicker,
        previouslyAssigned: (
          <PreviouslyAssignedTooltip previouslyAssignedActivityData={previouslyAssignedActivityData?.[id]} />
        ),
        removable: true,
      };

      // add a separate row for the "Copy to All" buttons below the first activity row
      if (i === 0 && activities.length > 1) {
        return [
          dataRow,
          {
            id: '',
            className: '',
            tool: '',
            activity: '',
            activityFocus: '',
            dueDatePicker: (<CopyToAllButton handleClickCopyToAll={copyDueDateToAll} />),
            publishDatePicker: (<CopyToAllButton handleClickCopyToAll={copyPublishDateToAll} />),
            removable: false
          },
        ];
      }

      return [dataRow];
    });
  }

  return (
    <section aria-labelledby="reviewActivitiesHeader" className="assignment-section review-activities-section">
      <Snackbar text={INVALID_DATES_SNACKBAR_COPY} visible={snackbarVisible} />
      <div className="assignment-section-header">
        <span className="assignment-section-number">2</span>
        <span className="assignment-section-name" id="reviewActivitiesHeader">Review activities and pick due dates</span>
      </div>
      <div className="assignment-section-body">
        <DataTable
          headers={tableHeaders}
          removeHeaderText="Remove"
          removeRow={removeRow}
          reportingFormat={true}
          rows={rows()}
          showRemoveIcon
        />
      </div>
    </section>
  )
}

export default ReviewActivities
